import React from "react";
import { Paper, Typography, Stack, Box, LinearProgress } from "@mui/material";

const Sparkline = ({ history, width = 260, height = 46 }) => {
  if (!history || history.length < 2) return null;
  const max = Math.max(1, ...history.map((h) => h.burntTiles));
  const pts = history
    .map((h, i) => {
      const x = (i / (history.length - 1)) * (width - 4) + 2;
      const y = height - 4 - (h.burntTiles / max) * (height - 8);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
  const last = history[history.length - 1];
  return (
    <Box>
      <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
        <polyline
          points={`2,${height - 4} ${pts} ${width - 2},${height - 4}`}
          fill="rgba(255,138,61,0.15)"
          stroke="none"
        />
        <polyline points={pts} fill="none" stroke="#ff8a3d" strokeWidth="1.5" />
      </svg>
      <Typography variant="caption" color="text.secondary">
        Burned footprint over time · now {last.burntTiles.toLocaleString()} cells
      </Typography>
    </Box>
  );
};

const Kpi = ({ label, value, sub, color }) => (
  <Box sx={{ minWidth: 100, flexGrow: 1 }}>
    <Typography variant="overline" color="text.secondary" sx={{ fontSize: "0.62rem", lineHeight: 1.4 }}>
      {label}
    </Typography>
    <Typography variant="h6" sx={{ lineHeight: 1.1, color: color || "text.primary" }}>
      {value}
    </Typography>
    {sub && (
      <Typography variant="caption" color="text.secondary">
        {sub}
      </Typography>
    )}
  </Box>
);

const ImpactCard = ({ stats, history, cellSizeM, totalStructures }) => {
  const initial = stats?.initial || 0;
  const current = stats?.current || 0;
  const pct = initial > 0 ? (current / initial) * 100 : 100;
  const burntKm2 = ((stats?.totalBurntTiles || 0) * (cellSizeM || 0) ** 2) / 1e6;
  const estStructuresLost =
    initial > 0 && totalStructures
      ? Math.round(((initial - current) / initial) * totalStructures)
      : 0;
  const valuePct =
    stats?.totalValue > 0 ? (stats.valueLost / stats.totalValue) * 100 : 0;

  return (
    <Paper sx={{ p: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline">
        <Typography variant="overline" color="text.secondary">
          Impact
        </Typography>
        {stats?.burning > 0 && (
          <Typography variant="caption" sx={{ color: "error.main", fontWeight: 700 }}>
            ● {stats.burning.toLocaleString()} {stats.burning === 1 ? "cell" : "cells"} alight
          </Typography>
        )}
      </Stack>
      <Box sx={{ mt: 1, mb: 1.5 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="baseline">
          <Typography variant="subtitle2">Infrastructure intact</Typography>
          <Typography variant="subtitle2" sx={{ color: pct > 90 ? "success.main" : pct > 70 ? "warning.main" : "error.main" }}>
            {pct.toFixed(1)}%
          </Typography>
        </Stack>
        <LinearProgress
          variant="determinate"
          value={pct}
          color={pct > 90 ? "success" : pct > 70 ? "warning" : "error"}
          sx={{ height: 8, borderRadius: 4, mt: 0.5 }}
        />
      </Box>
      <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
        <Kpi
          label="Est. structures lost"
          value={estStructuresLost.toLocaleString()}
          color={estStructuresLost > 0 ? "error.main" : "success.main"}
        />
        <Kpi label="Burned area" value={`${burntKm2.toFixed(1)} km²`} />
        <Kpi
          label="Asset value lost"
          value={`${valuePct.toFixed(1)}%`}
          sub="value-weighted"
        />
      </Stack>
      <Box sx={{ mt: 1.5 }}>
        <Sparkline history={history} />
      </Box>
    </Paper>
  );
};

export default ImpactCard;
